import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {Mental} from "../Mental";
import {TweenLite} from "gsap";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";

export class MentalXSplitAnimationsView extends PIXI.Container implements StageLayer {
    private _bg: PIXI.Sprite;
    private _borderTopFrame: PIXI.Sprite;
    private _borderBottomFrame: PIXI.Sprite;
    private _xSplitLogoAnimation: PIXI.spine.Spine;
    private _bgEnhancer: PIXI.Sprite;
    private _borderTopFrameEnhancer: PIXI.Sprite;
    private _borderBottomFrameEnhancer: PIXI.Sprite;
    private _xSplitLogoAnimationEnhancer: PIXI.spine.Spine;
    private _knifeItems: PIXI.spine.Spine[] = []; // 1 item for each reel.
    private _bloodItems: TimelineSprite[] = []; // 1 item for each reel/knife.
    private _bloodTween: TweenLite[] = [];

    constructor() {
        super();

        this.init();
    }

    private static resetSpineItem(item: PIXI.spine.Spine, yPos: number): void {
        item.y = yPos;

        item.skeleton.setSlotsToSetupPose();
        item.skeleton.setToSetupPose();
        item.state.clearTracks();
        item.state.setEmptyAnimation(0, 0);

        if (!item.visible) {
            item.visible = true;
            item.updateTransform();
        }
    }

    public stopAllAnimationsNormal(): void {
        this.removeLogoItems(this._bg, this._borderTopFrame, this._borderBottomFrame, this._xSplitLogoAnimation);

        this.removeKnifeAndBlood();
    }

    public stopAllAnimationsEnhancer(): void {
        this.removeLogoItems(this._bgEnhancer, this._borderTopFrameEnhancer, this._borderBottomFrameEnhancer, this._xSplitLogoAnimationEnhancer);

        this.removeKnifeAndBlood();
    }

    public playKnifeAnimation(reelId: number, yPos: number): void {
        Mental.xWaysCounter.updateXwaysCounterFromXSplit(reelId);
        const knifeItem: PIXI.spine.Spine = this._knifeItems[reelId],
            bloodItem: TimelineSprite = this._bloodItems[reelId],
            timeScale: number = (knifeItem.skeleton.data.findAnimation("cutting_animation").duration / Mental.timeSegment);

        MentalXSplitAnimationsView.resetSpineItem(knifeItem, yPos);
        bloodItem.y = knifeItem.y;

        knifeItem.state.setAnimation(0, "cutting_animation", false).timeScale = timeScale;
        knifeItem.alpha = 0;
        TweenLite.to(knifeItem, {alpha: 1, duration: 0.2});

        this._bloodTween.push(TweenLite.to({}, {
            duration: (12 / 30), onComplete: () => {
                bloodItem.alpha = 0;
                TweenLite.to(bloodItem, {alpha: 1, duration: 0.2}).timeScale(timeScale);
                bloodItem.getAnimationAutoShowHide(true, true, [0, 33]).timeScale(timeScale);
            }
        }).timeScale(timeScale));
    }

    public playXSplitLogoAnimationNormal(reelId: number, height: number, yPos: number): void {
        this._bg.height = this._borderBottomFrame.height = height;
        MentalXSplitAnimationsView.resetSpineItem(this._xSplitLogoAnimation, yPos);
        this._bg.y = this._xSplitLogoAnimation.y;

        this.setXSplitLogoAnimation(reelId, height, this._bg, this._borderTopFrame, this._borderBottomFrame, this._xSplitLogoAnimation);
    }

    public playXSplitLogoAnimationEnhancer(reelId: number): void {
        const height: number = 99; // enhancer height.
        this._bgEnhancer.height = this._borderBottomFrameEnhancer.height = height;
        MentalXSplitAnimationsView.resetSpineItem(this._xSplitLogoAnimationEnhancer, 0);
        this._bgEnhancer.y = this._xSplitLogoAnimationEnhancer.y = 634.5; // this is the y-position for enhancer xSplit.

        this.setXSplitLogoAnimation(reelId, height, this._bgEnhancer, this._borderTopFrameEnhancer, this._borderBottomFrameEnhancer, this._xSplitLogoAnimationEnhancer);
    }

    private init(): void {
        this.createBackgroundAndBorderForEnhancer();
        this._xSplitLogoAnimationEnhancer = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("xSplit"));
        this._xSplitLogoAnimationEnhancer.visible = false;
        this.addChild(this._xSplitLogoAnimationEnhancer);

        this.createBackgroundAndBorder();
        this._xSplitLogoAnimation = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("xSplit"));
        this._xSplitLogoAnimation.visible = false;
        this.addChild(this._xSplitLogoAnimation);

        this.createKnifeAndBloodItems();
    }

    private createBackgroundAndBorderForEnhancer(): void {
        this._bgEnhancer = new PIXI.Sprite(Mental.assetLoader.getTextures("xSplitBackground")[0]);
        this._bgEnhancer.visible = false;
        this._bgEnhancer.anchor.set(0.5);

        this._borderTopFrameEnhancer = new PIXI.Sprite(Mental.assetLoader.getTextures("xWaysBottomFrame")[0]);
        this._borderTopFrameEnhancer.visible = false;
        this._borderTopFrameEnhancer.anchor.set(0.5, 0); // keep it top anchored, it helps in positioning this item.

        this._borderBottomFrameEnhancer = new PIXI.Sprite(Mental.assetLoader.getTextures("xWaysTopFrame")[0]);
        this._borderBottomFrameEnhancer.visible = false;
        this._borderBottomFrameEnhancer.anchor.set(0.5, 1); // keep it bottom anchored, it helps in positioning this item.

        this.addChild(this._bgEnhancer, this._borderTopFrameEnhancer, this._borderBottomFrameEnhancer);
    }

    private createBackgroundAndBorder(): void {
        this._bg = new PIXI.Sprite(Mental.assetLoader.getTextures("xSplitBackground")[0]);
        this._bg.visible = false;
        this._bg.anchor.set(0.5);

        this._borderTopFrame = new PIXI.Sprite(Mental.assetLoader.getTextures("xWaysBottomFrame")[0]);
        this._borderTopFrame.visible = false;
        this._borderTopFrame.anchor.set(0.5, 0); // keep it top anchored, it helps in positioning this item.

        this._borderBottomFrame = new PIXI.Sprite(Mental.assetLoader.getTextures("xWaysTopFrame")[0]);
        this._borderBottomFrame.visible = false;
        this._borderBottomFrame.anchor.set(0.5, 1); // keep it bottom anchored, it helps in positioning this item.

        this.addChild(this._bg, this._borderTopFrame, this._borderBottomFrame);
    }

    private createKnifeAndBloodItems(): void {
        const spineName: string = "xSplitKnife",
            numReels: number = 5,
            symWidth: number = 144;

        for (let i = 0; i < numReels; i++) {
            const knifeItem: PIXI.spine.Spine = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset(spineName));
            knifeItem.visible = false;
            this._knifeItems.push(knifeItem);
            knifeItem.x = ((i * symWidth) + (symWidth / 2));

            const bloodItem: TimelineSprite = new TimelineSprite(Mental.assetLoader.getTextures("xSplitBlood"));
            bloodItem.hide();
            bloodItem.anchor.set(0, 0.5);
            bloodItem.scale.set(1.1, 1); // #168
            bloodItem.x = knifeItem.x - (bloodItem.width / 2) + 10; // #168
            this._bloodItems.push(bloodItem);

            this.addChild(knifeItem, bloodItem);
        }
    }

    private setXSplitLogoAnimation(reelId: number, height: number, bg: PIXI.Sprite, borderTopFrame: PIXI.Sprite, borderBottomFrame: PIXI.Sprite, xSplitLogoAnimation: PIXI.spine.Spine): void {
        const symWidth: number = 144;

        bg.visible = true;
        bg.alpha = 0;

        borderTopFrame.visible = true;
        borderTopFrame.alpha = 0;

        borderBottomFrame.visible = true;
        borderBottomFrame.alpha = 0;

        xSplitLogoAnimation.visible = true;
        xSplitLogoAnimation.alpha = 0;

        bg.x = borderTopFrame.x = borderBottomFrame.x = xSplitLogoAnimation.x = (reelId * symWidth) + (symWidth / 2);
        borderTopFrame.y = (xSplitLogoAnimation.y - (height / 2));
        borderBottomFrame.y = (xSplitLogoAnimation.y + (height / 2));

        TweenLite.to([bg, borderTopFrame, borderBottomFrame, xSplitLogoAnimation], {
            alpha: 1,
            duration: 0.2
        });

        xSplitLogoAnimation.state.setAnimation(0, "landing_animation", false);
    }

    private removeLogoItems(bg: PIXI.Sprite, borderTopFrame: PIXI.Sprite, borderBottomFrame: PIXI.Sprite, xSplitLogoAnimation: PIXI.spine.Spine): void {
        TweenLite.to([bg, borderTopFrame, borderBottomFrame, xSplitLogoAnimation], {
            alpha: 0, duration: 0.2, onComplete: () => {
                bg.visible = false;
                borderTopFrame.visible = false;
                borderBottomFrame.visible = false;

                xSplitLogoAnimation.visible = false;
                xSplitLogoAnimation.state.clearTracks();
            }
        });
    }

    private removeKnifeAndBlood(): void {
        for (let i: number = 0; i < this._knifeItems.length; i++) {
            const knifeItem: PIXI.spine.Spine = this._knifeItems[i],
                bloodItem: TimelineSprite = this._bloodItems[i];

            TweenLite.to([knifeItem, bloodItem], {
                alpha: 0, duration: 0.2, onComplete: () => {
                    knifeItem.visible = false;
                    knifeItem.state.clearTracks();

                    bloodItem.hide();
                }
            });
        }

        if (this._bloodTween.length > 0) {
            for (let i: number = 0; i < this._bloodTween.length; i++) {
                this._bloodTween[i].kill();
            }
        }
        this._bloodTween = [];
    }
}